/**
 * @fileoverview Drawer wrapper around the legacy `StudentModalContent`. The
 * page passes the cell context (session meeting, student, assessment); this
 * component fetches the assessment shape the legacy form expects and renders
 * it inside `LumiarDrawer`, then forwards saves back through `onSaved`.
 */

import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Center, Loader } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { Beforeunload } from 'react-beforeunload'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'
import { Modal, IKind } from 'components/ModalLegacy'
// eslint-disable-next-line max-len
import StudentModalContent from '@legacy/pages/SessionMeetingAssessment/components/StudentModalContent/StudentModalContent'
import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { useCurrentUser } from 'context'
import { useFeatureToggle } from 'hooks'
import { getActivity } from 'pages/NewProject/service/projects'
// eslint-disable-next-line max-len
import type { IStudent } from '@legacy/pages/SessionMeetingAssessment/components/StudentsAssessments/StudentsAssessmentsTypes'
import type { LessonActivityDetail } from './types'
import { ArcStudentAssessmentDrawerContent } from './ArcStudentAssessmentDrawerContent'
import type { IActivity } from 'interfaces'

/** Width of the legacy `StudentModalContent` (`@legacy/.../StudentModalContentStyled.ts`). */
const DRAWER_WIDTH = 700
const NOOP = () => {}

interface TaskTrackerAssessmentDrawerProps {
  opened: boolean
  sessionMeetingId: string
  studentId: string
  assessmentId: string | null
  activityTitle?: string
  lessonTitle?: string
  lessonDate?: string
  activities?: IActivity[]
  onClose: () => void
  onSaved?: () => void
}

export function TaskTrackerAssessmentDrawer({
  opened,
  sessionMeetingId,
  studentId,
  assessmentId,
  activities,
  onClose,
  onSaved,
}: TaskTrackerAssessmentDrawerProps) {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLElement | null>(null)

  const { currentUser } = useCurrentUser()
  const isArc = useFeatureToggle('arc-mosaico-project') as boolean

  const [loading, setLoading] = useState(true)
  const [selectedStudent, setSelectedStudent] = useState<IStudent | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [lessonActivityDetails, setLessonActivityDetails] = useState<LessonActivityDetail[]>([])

  // Stable key over the activity ids to avoid re-fetching when the parent
  // recreates the `activities` array reference on every render.
  const activityIdsKey = useMemo(
    () => (activities ?? []).map(a => a.id).join(','),
    [activities],
  )

  useEffect(() => {
    if (!opened || !activityIdsKey) {
      setLessonActivityDetails([])
      return
    }
    let cancelled = false
    const ids = activityIdsKey.split(',').filter(Boolean)
    Promise.all(ids.map(id => getActivity(id))).then(results => {
      if (cancelled) return
      const details: LessonActivityDetail[] = results
        .filter(Boolean)
        .map(r => ({
          id: r.id,
          title: r.title,
          planning: r.planning,
          objective: r.objective,
        }))
      setLessonActivityDetails(details)
    })
    return () => {
      cancelled = true
    }
  }, [opened, activityIdsKey])

  useEffect(() => {
    if (!opened || !sessionMeetingId || !studentId) {
      setLoading(false)
      return
    }

    const fetchStudentAssessment = async () => {
      setLoading(true)
      try {
        let finalAssessmentId = assessmentId
        if (!finalAssessmentId) {
          const response = await api.get(
            ARC_MOSAICO_ENDPOINTS.taskTrack.studentsByMeeting(sessionMeetingId),
          )
          const students = response.data
          if (!Array.isArray(students) || students.length === 0) {
            setSelectedStudent(null)
            return
          }
          const student = students.find((s: IStudent) => s.id === studentId)
          if (!student?.assessmentId) {
            setSelectedStudent(null)
            return
          }
          finalAssessmentId = student.assessmentId
        }

        const assessmentResponse = await api.get(
          ARC_MOSAICO_ENDPOINTS.taskTrack.studentAssessment(sessionMeetingId, finalAssessmentId!),
        )
        setSelectedStudent(assessmentResponse.data ?? null)
      } catch {
        setSelectedStudent(null)
      } finally {
        setLoading(false)
      }
    }

    fetchStudentAssessment()
  }, [opened, sessionMeetingId, studentId, assessmentId])

  useEffect(() => {
    if (!opened) {
      setSelectedStudent(null)
      setHasChanges(false)
    }
  }, [opened])

  /** Asks for confirmation when there are unsaved changes, then runs `onConfirmed`. */
  const confirmCloseIfDirty = useCallback(
    (onConfirmed: () => void) => {
      if (!hasChanges) {
        onConfirmed()
        return
      }
      Modal({
        title: t('students_assessment_title_modal'),
        text: t('students_assessment_text_modal'),
        kind: IKind.ConfirmationExit,
      }).then(({ isConfirmed }) => {
        if (isConfirmed) onConfirmed()
      })
    },
    [hasChanges, t],
  )

  const handleDrawerChange = useCallback(
    (value: boolean) => {
      if (value) return
      confirmCloseIfDirty(() => {
        setHasChanges(false)
        setSelectedStudent(null)
        onClose()
      })
    },
    [confirmCloseIfDirty, onClose],
  )

  /** Legacy `StudentModalContent` close handler. `didSave` is true only after `onSave`. */
  const handleCloseDrawer = useCallback(
    (value: boolean, didSave = false) => {
      if (value) return
      const close = () => {
        setSelectedStudent(null)
        if (didSave) onSaved?.()
        onClose()
      }
      if (didSave) close()
      else confirmCloseIfDirty(close)
    },
    [confirmCloseIfDirty, onClose, onSaved],
  )

  const showNoAssessment = (!studentId || !selectedStudent) && opened && !loading
  const isStudent = currentUser?.profile_used === 'student'
  const titleOverride = isArc && isStudent
    ? t('students_assessment_self_assessment_title')
    : undefined

  return (
    <>
      {hasChanges && opened && (
        <Beforeunload onBeforeunload={event => event.preventDefault()} />
      )}

      <LumiarDrawer
        visibility={opened}
        onChange={handleDrawerChange}
        side="right"
        modalRef={modalRef as RefObject<HTMLDivElement>}
        scrollable={false}
      >
        <Box
          style={{
            width: DRAWER_WIDTH,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {/* Wait for currentUser before picking the content: rendering the
                legacy teacher modal while the profile is still resolving would
                flash the teacher's scores to students (AR-770). */}
            {loading || (isArc && !currentUser) ? (
              <Center style={{ width: '100%' }}>
                <Loader size="md" />
              </Center>
            ) : showNoAssessment ? (
              <Box p="md" style={{ width: '100%', textAlign: 'center', paddingTop: 40 }}>
                {t('task_tracker.drawer.no_assessment')}
              </Box>
            ) : selectedStudent ? (
              isArc && isStudent ? (
                <ArcStudentAssessmentDrawerContent
                  sessionMeetingSetState={NOOP}
                  sessionMeetingState={{ session_meeting: { id: sessionMeetingId } }}
                  meetingAssessmentPayload={{}}
                  hasChangesNotSavedOnMeetingAssessment={false}
                  selectedStudent={selectedStudent}
                  hasChanges={hasChanges}
                  goNext={false}
                  handleCloseDrawer={handleCloseDrawer}
                  mixPanelTracker={NOOP}
                  saveAndGoNext={NOOP}
                  setStudents={NOOP}
                  setSelectedStudent={setSelectedStudent}
                  setHasChanges={setHasChanges}
                  modalRef={modalRef}
                  loadingDrawer={false}
                  sessionMeetingId={sessionMeetingId}
                  titleOverride={titleOverride}
                  lessonActivities={lessonActivityDetails}
                />
              ) : (
                <StudentModalContent
                  sessionMeetingSetState={NOOP}
                  sessionMeetingState={{ session_meeting: { id: sessionMeetingId } }}
                  meetingAssessmentPayload={{}}
                  hasChangesNotSavedOnMeetingAssessment={false}
                  selectedStudent={selectedStudent}
                  hasChanges={hasChanges}
                  goNext={false}
                  handleCloseDrawer={handleCloseDrawer}
                  mixPanelTracker={NOOP}
                  saveAndGoNext={NOOP}
                  setStudents={NOOP}
                  setSelectedStudent={setSelectedStudent}
                  setHasChanges={setHasChanges}
                  modalRef={modalRef}
                  loadingDrawer={false}
                  sessionMeetingId={sessionMeetingId}
                />
              )
            ) : (
              <Box p="md" style={{ width: '100%', textAlign: 'center', paddingTop: 40 }}>
                {t('task_tracker.drawer.error_loading')}
              </Box>
            )}
          </Box>
        </Box>
      </LumiarDrawer>
    </>
  )
}
